import { el, money } from './ui'
import { askedBadges, extraBadges, extrasTotalOf } from './extras'

// A visit with the insides done as well (0070, Liam 13 Oct 2026: "we need a way of more clearly seeing inside price and
// outside price at the moment easy to get missed on the tile"). Its whole price stays amount_pence; inside_pence is the
// insides part of it. Wherever one visit's price is shown it reads "Outside £45 · Inside £40", never one figure that
// hides the insides; on a card the insides get their own badge so they can't be missed.

export interface InsidesPriced {
  amount_pence?: number | null
  inside_pence?: number | null
  // Extras for this visit (0087, src/extras.ts): inside amount_pence too, shown as their own badges.
  extras_json?: string | null
  // A helper's extras waiting for a price (0091).
  extras_asked_json?: string | null
}

export function hasInsides(v: InsidesPriced): boolean {
  return typeof v.inside_pence === 'number' && v.inside_pence > 0 && typeof v.amount_pence === 'number'
}

export function outsidePence(v: InsidesPriced): number | null {
  if (v.amount_pence === null || v.amount_pence === undefined) return null
  // The job's own price: the whole less its insides and its extras (0087).
  return v.amount_pence - (hasInsides(v) ? (v.inside_pence as number) : 0) - extrasTotalOf(v.extras_json)
}

// "£85.00", or "Outside £45.00 · Inside £40.00"; null with no price.
export function priceWords(v: InsidesPriced): string | null {
  if (v.amount_pence === null || v.amount_pence === undefined) return null
  // Extras (0087): the whole, then what is in it, so none of it is missed (GPT-6.1's extras review 7).
  const extras = extrasTotalOf(v.extras_json)
  if (!hasInsides(v)) return extras ? `${money(v.amount_pence)} (with extras ${money(extras)})` : money(v.amount_pence)
  return `Outside ${money(outsidePence(v) as number)} · Inside ${money(v.inside_pence as number)}${extras ? ` · Extras ${money(extras)}` : ''}`
}

// The price badges on a card: one, or Outside and Inside side by side, the insides in their own colour.
export function priceBadges(v: InsidesPriced): HTMLElement[] {
  // Extras show as the work even with prices hidden (no amount on a worker's phone): "+ Conservatory roof · 4 panels".
  const extras = [...extraBadges(v.extras_json), ...askedBadges(v.extras_asked_json)]
  if (v.amount_pence === null || v.amount_pence === undefined) return extras
  if (!hasInsides(v)) return extras.length ? [el('span', { class: 'badge' }, money(outsidePence(v) as number)), ...extras] : [el('span', { class: 'badge' }, money(v.amount_pence))]
  return [
    el('span', { class: 'badge' }, `Outside ${money(outsidePence(v) as number)}`),
    el('span', { class: 'badge badge-insides' }, `Inside ${money(v.inside_pence as number)}`),
    ...extras,
  ]
}

// Part done (0070): "Fronts only" on a visit done as a part, "Backs (the rest)" on the visit booked for the rest, so the
// card says it without opening the note (Codex). Null on a whole job.
export function partBadge(v: { part?: string | null; rest_of?: number | null }): HTMLElement | null {
  if (!v.part) return null
  const words = v.part.charAt(0).toUpperCase() + v.part.slice(1)
  return el('span', { class: 'badge badge-part' }, v.rest_of ? `${words} (the rest)` : `${words} only`)
}
